import {Component, ElementRef, EventEmitter, inject, Input, OnDestroy, OnInit, Output, ViewChild, ChangeDetectionStrategy} from '@angular/core';

import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {
  faCheck,
  faChevronDown,
  faCircleCheck,
  faCircleInfo,
  faClipboardCheck,
  faClipboardList,
  faFile,
  faFileExcel,
  faFilePdf,
  faFileWord,
  faMemoCircleCheck,
  faMinus,
  faPen,
  faPlay,
  faScrewdriverWrench,
  faSearch,
  faShieldCheck,
} from '@fortawesome/pro-solid-svg-icons';
import {faSpinnerThird} from '@fortawesome/pro-duotone-svg-icons';
import {faIfc} from "../../../util/icons/custom-icons";
import {forkJoin, of} from 'rxjs';
import {
  BIC_FILE_SERVICE_ADAPTER,
  BicButtonComponent,
  BicFile,
  BicFileManagerComponent,
  BicFileManagerConfig,
  BicFileServiceAdapter,
  BicFilterConfig,
  BicInputComponent,
  BicPopupComponent,
  TooltipDirective,
} from '@buildinginformationcloud/bic-ui-components';
import {ProjectFile, ProjectFileControllerService} from '../../../../api';
import {RulesetRule, RulesetService} from '../../../../services/ruleset.service';
import {Ruleset} from '../../../ruleManagement/models/rule-management.models';
import {StateConfigService} from '../../../../services/state/state-config.service';
import {DocumentClassService} from '../../../files/components/document-classes/document-class.service';
import {IdsAdapterService} from '../../../checks/bimCheck/components/ids/services/ids-adapter.service';
import {filesForRuleset, normalizeFileTypeToken, rulesetAllowedFileTypes} from '../../../../models/ruleset-file-match';
import {addMissingAncestorFolders} from '../../../../services/files/file-tree.utils';
import {createMemo} from '../../../../memo';

/**
 * Beschreibt, wie die kombinierte Auswahl initialisiert werden soll.
 * Wird von der Execution-Komponente aus dem Manifest abgeleitet und übergeben.
 */
export interface CombinedSelectionPlan {
  checkName: string;
  checkDescription: string;
  userHint: string;
  /** Anzeigetext des Auslösers (z. B. "Manuell"). */
  triggerLabel: string;
  ruleMode: 'manual' | 'predefined';
  fileMode: 'manual' | 'predefined';
  /** Erlaubte Regeltypen für die manuelle Auswahl (Tabs/Filter). */
  ruleTypes: string[];
  /** Im Builder fixierte Regelsets (Einschränkung im manuellen Modus / Anzeige bei vordefiniert). */
  selectedRuleSetIds: string[];
  /** Im Builder fixierte Einzelregeln. */
  selectedRuleIds: string[];
  /** Regelsets, bei denen im Builder nur einzelne Regeln (nicht das ganze Set) ausgewählt wurden. */
  partiallySelectedRuleSetIds: string[];
  /** Erlaubte Dateitypen. Leer = keine Einschränkung (alle Typen). */
  fileTypes: string[];
  /** Erlaubte Dokumentenklassen. Leer = keine Einschränkung (alle Klassen). */
  documentClasses: string[];
  /** Minimale Anzahl auswählbarer Dateien (aus den myCheck-Einstellungen). */
  minFiles?: number;
  /** Maximale Anzahl auswählbarer Dateien (aus den myCheck-Einstellungen). */
  maxFiles?: number;
  /**
   * Scope der vordefinierten Dateiauswahl.
   * 'all' = alle Projektdateien anzeigen (kein Typ-Filter).
   * 'selected' = nur Dateien der konfigurierten fileTypes anzeigen.
   * Nur relevant bei fileMode === 'predefined'.
   */
  predefinedFileSelectionScope?: 'all' | 'selected';
  /** Gesamttyp des Checks (für Hybrid-Handling). */
  rulesetType: string;
  /** Aktive Berichtsformate (z. B. ['EXCEL', 'PDF']). Leer = nur In-App. */
  reportFormats: string[];
  /** Berichtsperspektive (z. B. 'rules_first', 'files_first'). */
  reportPerspective: string;
  /**
   * True, wenn der myCheck über den Dispatcher (`multi_check_executor`) läuft, also **pro Regelset**
   * einen eigenen Sub-Run ausführt – und damit auch pro Regelset abgerechnet wird.
   *
   * Muss aus dem Manifest kommen und nicht aus der Laufzeit-Auswahl abgeleitet werden: nur wenn der
   * Dispatcher-Step wirklich im Manifest steht, bucht das Backend mehrere Einheiten ab.
   */
  usesMultiCheckDispatcher?: boolean;
}

/** Ergebnis der kombinierten Auswahl, das an die Execution-Komponente zurückgegeben wird. */
export interface CombinedSelectionResult {
  /** Resume-Payload für den interaktiven Regel-Schritt (nur bei manueller Regelauswahl). */
  ruleData?: Record<string, unknown>;
  /** Resume-Payload für den interaktiven Datei-Schritt (nur bei manueller Dateiauswahl). */
  fileData?: Record<string, unknown>;
  /**
   * Trigger-Daten für den Workflow-Start (nur bei vordefiniertem Dateimodus).
   * Enthält project_files, damit das Backend sie beim internen file_selection-Step bekommt.
   */
  triggerData?: Record<string, unknown>;
  /** Zusammenfassung der gewählten Regeln für den Ladebildschirm (gruppiert pro Regelset). */
  ruleSummaries: { name: string; type: string; ruleNames?: string[]; rulesetId?: string }[];
  /** Zusammenfassung der gewählten Dateien für den Ladebildschirm. */
  fileSummaries: { name: string; fileType: string; size?: number }[];
  /**
   * Frei vergebener Berichtsname für diesen Lauf; `undefined`, wenn der Nutzer keinen eingegeben
   * hat. Dann bleibt es beim bisherigen Verhalten: der Bericht trägt den Namen des myChecks.
   */
  reportName?: string;
}

/**
 * Die Auswahl eines einzelnen Regelsets – eine Gruppe. Entspricht dem `rule_groups`-Vertrag des
 * Backends, das je Gruppe einen Sub-Run mit der typrichtigen Kette startet.
 *
 * Bewusst snake_case: Der Eintrag wird unverändert als Step-Output bzw. Trigger-Datum
 * weitergegeben und dort von den bestehenden Ketten gelesen.
 */
export interface RuleGroupPayload {
  ruleset_id: string;
  ruleset_name: string;
  /** IDS | KI | HYBRID */
  ruleset_type: string;
  rule_ids: string[];
  rule_names: string[];
  /**
   * Nur bei einer IDS-Teilauswahl: die clientseitig auf die gewählten Spezifikationen reduzierte
   * XML. Eine einzelne Spezifikation hat keine im Backend auflösbare Regel-ID, deshalb reist der
   * Regelinhalt hier mit.
   */
  ids_xml?: string;
  /**
   * Die für dieses Regelset passenden Dateien (Schnitt der Auswahl mit `allowedFileTypes` bzw.
   * den Dokumentenklassen). Der Orchestrator gibt genau diese an den Sub-Run weiter.
   */
  project_files?: { id?: string; name?: string; link?: string; fileType?: string; size?: number }[];
}

/** Eine Gruppe (Regelset) mit ihren Regeln für die Anzeige. */
interface RuleGroup {
  ruleset: Ruleset;
  rules: RulesetRule[];
  loading: boolean;
}

@Component({
  selector: 'my-checks-step-combined-select',
  standalone: true,
  imports: [FaIconComponent, BicButtonComponent, BicInputComponent, BicFileManagerComponent, BicPopupComponent, TooltipDirective],
  templateUrl: './step-combined-select.component.html',
  styleUrls: ['./step-combined-select.component.scss'],
  host: {class: 'flex flex-col h-full w-full overflow-hidden'},
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      // Read-only Adapter: in der myCheck-Auswahl werden Dateien nur ausgewaehlt,
      // nicht hochgeladen/bearbeitet/geloescht. Dateien kommen ueber customFiles.
      provide: BIC_FILE_SERVICE_ADAPTER,
      useValue: {
        loadFiles: () => of([] as BicFile[]),
        createFolder: () => of({} as BicFile),
        deleteFiles: () => of(void 0),
        renameFile: () => of({} as BicFile),
        copyFiles: () => of([] as BicFile[]),
        moveFiles: () => of([] as BicFile[]),
        downloadFiles: () => of(void 0),
        uploadFiles: () => {
        },
        getUploadJobs$: () => of([]),
        canUpload: () => false,
        canEdit: () => false,
        canDelete: () => false,
      } as BicFileServiceAdapter,
    },
  ],
})
export class StepCombinedSelectComponent implements OnInit, OnDestroy {

  /** Stabile Rueckgabewerte fuer Template-Bindungen - siehe memo.ts. */
  private readonly memo = createMemo();

  @Input() plan!: CombinedSelectionPlan;
  @Input() projectId = 0;

  /** Emittiert die gesammelte Auswahl, wenn der Nutzer "Prüfung starten" klickt. */
  @Output() started = new EventEmitter<CombinedSelectionResult>();
  /** Emittiert beim Abbrechen. */
  @Output() cancelled = new EventEmitter<void>();

  // ── Regeln ──────────────────────────────────────────────────────────────
  ruleGroups: RuleGroup[] = [];
  rulesLoading = true;
  ruleSearch = '';
  /** Aufgeklappte Regelsets (per ID). */
  expandedRulesetIds = new Set<string>();
  /** Vollständig ausgewählte Regelsets. */
  selectedSetIds = new Set<string>();
  /** Einzeln ausgewählte Regeln. */
  selectedRuleIds = new Set<string>();

  // ── Dateien ─────────────────────────────────────────────────────────────
  files: ProjectFile[] = [];
  selectedFiles: ProjectFile[] = [];
  filesLoading = true;
  /** Snapshot der geladenen vordefinierten Dateien – wird in start() verwendet, um Race-Conditions zu vermeiden. */
  private predefinedFileSnapshot: ProjectFile[] = [];

  /** Auf BicFile gemappte Dateien fuer das bic-file-manager (Kompakt-Auswahl-View). */
  bicFiles: BicFile[] = [];
  /** Aktuelle Auswahl im bic-file-manager. */
  bicSelectedFiles: BicFile[] = [];
  /** Suchbegriff aus dem Suchfeld von bic-file-manager, mitgelesen über {@link onFileManagerSearchInput}. */
  fileSearchTerm = '';
  /** Aktive Spaltenfilter aus bic-file-filter, mitgelesen über {@link syncColumnFiltersFromFileManager}. */
  fileColumnFilters: Record<string, any[]> = {};
  /** An bic-file-manager gebundene Dateiliste (s. {@link updateDisplayedBicFiles}). */
  displayedBicFiles: BicFile[] = [];
  private lastColumnFiltersJson = '{}';
  private static readonly DATE_RANGE_OPTIONS = ['Heute', 'Letzte 7 Tage', 'Letzte 30 Tage', 'Dieses Jahr', 'Vor diesem Jahr'];

  @ViewChild(BicFileManagerComponent) private bicFileManagerRef?: BicFileManagerComponent;
  /** Aktuell mit {@link onFileManagerCaptureClick} verbundenes Element (s. Setter unten). */
  private fileManagerCaptureTarget?: HTMLElement;
  /**
   * Setter statt einfachem `@ViewChild`-Property: solange Dateien laden, existiert
   * `<bic-file-manager>` noch nicht (im `@else`-Zweig), ein einmaliges `ngAfterViewInit` liefe
   * also ins Leere. Der Setter feuert erneut, sobald das Element nach dem Laden erscheint.
   */
  @ViewChild(BicFileManagerComponent, {read: ElementRef})
  private set bicFileManagerElRef(ref: ElementRef<HTMLElement> | undefined) {
    this.fileManagerCaptureTarget?.removeEventListener('click', this.onFileManagerCaptureClick, true);
    this.fileManagerCaptureTarget = ref?.nativeElement;
    this.fileManagerCaptureTarget?.addEventListener('click', this.onFileManagerCaptureClick, true);
  }

  /** Bound reference for add/removeEventListener (s. {@link ngOnDestroy}). */
  private readonly onFileManagerCaptureClick = (): void => {
    setTimeout(() => this.syncColumnFiltersFromFileManager());
  };

  private documentClassTextContainerEl?: HTMLDivElement;
  private documentClassTextResizeObserver?: ResizeObserver;

  /** Setter statt `@ViewChild`-Property: der Text-Container existiert nur im entsprechenden `@if`-Zweig. */
  @ViewChild('documentClassTextContainer', {read: ElementRef})
  private set documentClassTextContainerRef(ref: ElementRef<HTMLDivElement> | undefined) {
    this.documentClassTextResizeObserver?.disconnect();
    this.documentClassTextContainerEl = ref?.nativeElement;
    if (this.documentClassTextContainerEl) {
      this.documentClassTextResizeObserver = new ResizeObserver(() => this.calculateDocumentClassTextOverflow());
      this.documentClassTextResizeObserver.observe(this.documentClassTextContainerEl);
    }
  }

  /** Konfiguration des bic-file-manager (Kompakt-Auswahl-View mit Filtermöglichkeiten). */
  fileManagerConfig: BicFileManagerConfig = {
    columns: [
      {key: 'name', label: 'Name', type: 'text'},
      {key: 'size', label: 'Größe', type: 'filesize'},
      {key: 'fileType', label: 'Typ', type: 'text', filterable: true},
      {key: 'documentClassTitles', label: 'Dokumentenklasse', type: 'text', filterable: true},
      {key: 'lastModified', label: 'Zuletzt geändert', type: 'date', filterable: true},
      {key: 'createdBy', label: 'Ersteller*in', type: 'text', filterable: true},
    ],
    enableUpload: false,
    enableContextMenu: false,
  };

  /** Ersetzt bic-file-manager's eigene, auf die aktuelle Ordnerebene beschränkte Filteroptionen
   *  durch projektweite Werte aus {@link bicFiles}. */
  get fileFilterConfigs(): BicFilterConfig[] {
    // Gecacht, weil das Ergebnis an [filterConfigs] haengt (siehe memo.ts). Die
    // Spalten sind konstant; die Optionen bleiben trotzdem aktuell, weil getOptions
    // `this.bicFiles` erst beim Aufruf liest.
    return this.memo('fileFilterConfigs', [this.fileManagerConfig.columns], () => this.fileManagerConfig.columns
      .filter(col => col.filterable)
      .map(col => col.type === 'date'
        ? {key: col.key, displayName: col.label, type: col.type, options: StepCombinedSelectComponent.DATE_RANGE_OPTIONS}
        : {
          key: col.key,
          displayName: col.label,
          type: col.type,
          getOptions: () => Array.from(new Set(
            this.bicFiles.flatMap(f => {
              const value = (f as any)[col.key];
              return Array.isArray(value) ? value : [value];
            }),
          )).filter(v => v != null),
        }));
  }

  confirming = false;

  /**
   * Optionaler, frei vergebener Berichtsname aus dem Bestätigungs-Popup. Leer heißt „nicht
   * vergeben" – gesendet wird dann {@link defaultReportName} statt eines leeren Werts, siehe
   * start().
   */
  reportName = '';

  /** Spaltenbreite von `workflow_instance.report_name`; das Backend kürzt zusätzlich. */
  readonly reportNameMaxLength = 255;
  showConfirmPopup = false;
  /** Blendet das Berichtsname-Feld im Bestätigungs-Popup ein; startet zugeklappt, siehe showConfirmation(). */
  showReportNameInput = false;
  /** Prüfzusammenfassung ein-/ausklappbar – erlaubt mehr Platz für die Auswahllisten auf kleinen Screens. */
  summaryCollapsed = false;
  /** True, solange die ID→Titel-Zuordnung der Dokumentenklassen noch lädt (für Ladeanzeige in der Zusammenfassung). */
  documentClassesLoading = true;

  protected readonly faCircleCheck = faCircleCheck;
  protected readonly faCheck = faCheck;
  protected readonly faMinus = faMinus;
  protected readonly faChevronDown = faChevronDown;
  protected readonly faClipboardCheck = faClipboardCheck;
  protected readonly faClipboardList = faClipboardList;
  protected readonly faScrewdriverWrench = faScrewdriverWrench;
  protected readonly faMemoCircleCheck = faMemoCircleCheck;
  protected readonly faFile = faFile;
  protected readonly faFilePdf = faFilePdf;
  protected readonly faFileExcel = faFileExcel;
  protected readonly faFileWord = faFileWord;
  protected readonly faIfc = faIfc;
  protected readonly faFileSlides = faFile; // kein dediziertes PowerPoint-Icon verfügbar
  protected readonly faSearch = faSearch;
  protected readonly faPlay = faPlay;
  protected readonly faPen = faPen;
  protected readonly faShieldCheck = faShieldCheck;
  protected readonly faCircleInfo = faCircleInfo;
  protected readonly faSpinnerThird = faSpinnerThird;

  private rulesetService = inject(RulesetService);
  private idsAdapter = inject(IdsAdapterService);
  private fileService = inject(ProjectFileControllerService);
  private documentClassService = inject(DocumentClassService);
  protected stateConfigService = inject(StateConfigService);
  private documentClassTitleById = new Map<string, string>();

  get isRuleManual(): boolean {
    return this.plan?.ruleMode === 'manual';
  }

  get isFileManual(): boolean {
    return this.plan?.fileMode === 'manual';
  }

  ngOnInit(): void {
    this.loadRules();
    // Dateien erst laden, wenn die Dokumentenklassen-Titel (ID → Titel) vorliegen: loadFiles()
    // muss die im Plan hinterlegten Klassen-IDs in Titel auflösen können, um sie mit den
    // Titeln aus file.documentClassTitles zu vergleichen.
    this.loadDocumentClasses(() => this.loadFiles());
  }

  ngOnDestroy(): void {
    this.fileManagerCaptureTarget?.removeEventListener('click', this.onFileManagerCaptureClick, true);
    this.documentClassTextResizeObserver?.disconnect();
  }

  // ── Laden ────────────────────────────────────────────────────────────────

  private loadRules(): void {
    this.rulesLoading = true;
    this.rulesetService.listRulesets(this.projectId).subscribe({
      next: (result) => {
        const all = [...result.prebuilt, ...result.custom];
        const filtered = this.filterRulesetsByPlan(all);
        this.ruleGroups = filtered.map(ruleset => ({ruleset, rules: [], loading: false}));

        if (this.plan.ruleMode === 'predefined') {
          // Vordefiniert: Regeln direkt nachladen, um sie read-only anzuzeigen.
          this.ruleGroups.forEach(group => {
            const isPartial = (this.plan.partiallySelectedRuleSetIds ?? []).includes(group.ruleset.id);
            this.loadGroupRules(group, isPartial ? 'partial' : 'full');
          });
        } else {
          // Manuell: teilausgewählte Sets sofort laden, damit Zähler/Badges von Anfang an
          // die gefilterte Anzahl zeigen statt erst nach dem Aufklappen.
          this.ruleGroups.forEach(group => {
            if (this.isPartiallyRestrictedSet(group.ruleset.id)) {
              this.loadGroupRules(group);
            }
          });
        }
        this.rulesLoading = false;
      },
      error: () => {
        this.ruleGroups = [];
        this.rulesLoading = false;
      },
    });
  }

  /**
   * Schränkt die angezeigten Regelsets gemäß Plan ein:
   * - vordefiniert: nur die fixierten Sets/Regeln
   * - manuell mit Einschränkung: nur die im Builder erlaubten Sets/Regeln
   * - manuell ohne Einschränkung: nach erlaubten Regeltypen
   */
  private filterRulesetsByPlan(all: Ruleset[]): Ruleset[] {
    // selectedRuleIds enthält Regel-IDs, keine Regelset-IDs – die dürfen hier nicht mit rs.id
    // verglichen werden. Teilausgewählte Sets werden über partiallySelectedRuleSetIds erkannt.
    const allowedIds = new Set<string>([
      ...this.plan.selectedRuleSetIds,
      ...(this.plan.partiallySelectedRuleSetIds ?? []),
    ]);
    if (allowedIds.size > 0) {
      return all.filter(rs => allowedIds.has(rs.id));
    }
    // Ohne explizite Set-Vorgabe nur die myChecks aus dem "myCheck"-Kontext anzeigen
    // (eigene/duplizierte), nicht die global bereitgestellten Katalog-Sets.
    const ownContextRulesets = all.filter(rs => !rs.isPrebuilt);
    if (this.plan.ruleTypes && this.plan.ruleTypes.length > 0) {
      return ownContextRulesets.filter(rs => this.plan.ruleTypes.includes(rs.type));
    }
    return ownContextRulesets;
  }

  private loadGroupRules(group: RuleGroup, preselectMode: 'none' | 'full' | 'partial' = 'none'): void {
    if (group.rules.length > 0 || group.loading) return;
    group.loading = true;
    this.rulesetService.getRulesetRules(group.ruleset, this.projectId).subscribe({
      next: (rules) => {
        // Bei einer Teilauswahl aus dem Builder nur die dort tatsächlich gewählten
        // Einzelregeln anzeigen/auswählbar machen – nicht alle Regeln des Sets.
        const visibleRules = this.isPartiallyRestrictedSet(group.ruleset.id)
          ? rules.filter(r => this.plan.selectedRuleIds.includes(r.id))
          : rules;
        // Alphabetisch nach Kürzel (Fallback: Name, z.B. bei IDS ohne Kürzel).
        visibleRules.sort((a, b) => (a.code ?? a.name).localeCompare(b.code ?? b.name, 'de', {numeric: true}));
        group.rules = visibleRules;
        group.loading = false;
        if (preselectMode === 'full') {
          this.selectedSetIds.add(group.ruleset.id);
        } else if (preselectMode === 'partial') {
          visibleRules.forEach(rule => this.selectedRuleIds.add(rule.id));
        }
      },
      error: () => {
        group.rules = [];
        group.loading = false;
      },
    });
  }

  /** True, wenn im Builder für dieses Set nur einzelne Regeln (nicht das ganze Set) gewählt wurden. */
  private isPartiallyRestrictedSet(rulesetId: string): boolean {
    return (this.plan.partiallySelectedRuleSetIds ?? []).includes(rulesetId)
      && !this.plan.selectedRuleSetIds.includes(rulesetId);
  }

  /** Tatsächliche Regelanzahl eines Sets: bei Teilauswahl nur die erlaubten (gefilterten) Regeln,
   *  sonst die volle Set-Größe. Wird für alle Anzeige-Zähler verwendet. */
  protected effectiveRuleCount(group: RuleGroup): number {
    if (this.isPartiallyRestrictedSet(group.ruleset.id)) {
      return group.rules.length;
    }
    return Math.max(group.ruleset.ruleCount, group.rules.length);
  }

  get documentClassSummary(): string {
    const titles = this.documentClassChips;
    return titles.length ? titles.join(', ') : 'Alle erlaubt';
  }

  /** Einzelne Klassen-Titel für die Ein-Zeilen-Anzeige. Leer = "Alle erlaubt" (s. {@link documentClassSummary}). */
  get documentClassChips(): string[] {
    const ids = this.plan?.documentClasses ?? [];
    if (!ids.length) {
      return [];
    }
    // Auch wenn im Plan explizit jede einzelne Klasse gelistet ist (statt einer leeren Auswahl für
    // "keine Einschränkung"), soll das wie "keine Einschränkung" als "Alle erlaubt" angezeigt werden.
    const selectedIds = ids.map(id => this.normalizeClassToken(id));
    const allIds = Array.from(this.documentClassTitleById.keys());
    if (this.areAllValuesSelected(selectedIds, allIds)) {
      return [];
    }
    // Nur auf Titel auflösbare Klassen anzeigen. Solange die ID→Titel-Zuordnung noch lädt
    // (oder eine ID unbekannt ist), bewusst leer lassen statt interne IDs auszugeben.
    return ids
      .map(id => this.documentClassTitleById.get(this.normalizeClassToken(id)))
      .filter((title): title is string => !!title);
  }

  /**
   * Begrenzt die Klassen-Anzeige auf eine Zeile: überlaufende Titel werden versteckt und durch
   * einen "+N"-Zähler ersetzt. Läuft auch der Zähler selbst noch über, wird ein weiterer Titel versteckt.
   */
  private calculateDocumentClassTextOverflow(): void {
    const container = this.documentClassTextContainerEl;
    if (!container) return;

    const items = Array.from(container.children) as HTMLElement[];
    const counter = container.parentElement?.querySelector('.overflow-counter') as HTMLElement | null;

    if (!items.length) {
      if (counter) counter.style.display = 'none';
      return;
    }

    items.forEach(el => el.style.removeProperty('display'));
    if (counter) counter.style.display = 'none';

    let hiddenCount = 0;
    let i = items.length - 1;
    while (container.scrollWidth > container.clientWidth && i >= 0) {
      items[i].style.display = 'none';
      hiddenCount++;
      i--;
      if (counter) {
        counter.textContent = '+' + hiddenCount;
        counter.style.display = '';
      }
    }
  }

  /**
   * Gesamtzahl auswählbarer (Nicht-Ordner-)Dateien im aktuellen Datei-Pool. Wird für eine eigene,
   * korrekte "X von Y ausgewählt"-Anzeige verwendet, weil der interne Zähler von bic-file-manager
   * (`formatSelectedOf()`/Header) bei reiner Ordner-Auswahl "0 von 0" zeigen kann: die installierte
   * Version cascadet die Auswahl eines Ordners nicht automatisch auf seine Dateien, und der Zähler
   * zählt sowohl "ausgewählt" als auch "gesamt" nur innerhalb der GERADE angezeigten Ebene, exklusive
   * Ordner. Das ist eine bestehende Bibliotheks-Einschränkung, keine Folge unserer Baum-Fixes.
   */
  get totalSelectableFileCount(): number {
    return this.files.filter(f => !this.isFolderFile(f)).length;
  }

  private normalizeClassToken(value: unknown): string {
    return String(value ?? '').trim().toLowerCase();
  }

  /** Mappt ProjectFile[] auf BicFile[] fuer das bic-file-manager. */
  private mapToBicFiles(files: ProjectFile[]): BicFile[] {
    return files.map(f => ({
      id: f.id,
      name: f.name,
      size: f.size,
      isFolder: f.isFolder ?? f.fileType === ProjectFile.FileTypeEnum.FOLDER,
      parentId: f.parentId,
      fileType: f.fileType,
      fileStatus: f.fileStatus,
      documentClassTitles: f.documentClassTitles ?? [],
      lastModified: f.lastModified ? new Date(f.lastModified as any) : undefined,
      createdBy: f.createdBy,
    }));
  }

  /**
   * True, wenn alle Dateitypen erlaubt sind. Ausschließlich an der tatsächlichen Typ-Abdeckung
   * festgemacht (leer = keine Einschränkung = alle). Der frühere `predefinedFileSelectionScope === 'all'`-
   * Kurzschluss wurde entfernt: im manuellen Dateimodus ist scope nicht gesetzt und fällt im Plan auf
   * 'all' zurück, wodurch fälschlich "Alle Dateitypen" angezeigt wurde, obwohl fileTypes eingeschränkt war.
   * Abgeglichen gegen die tatsächlich im Builder auswählbaren Typen (nicht `ALL_FILE_TYPES`, das auch
   * intern zugewiesene, nicht auswählbare Typen wie COORDINATION/UNKNOWN enthält).
   */
  get isAllFileTypes(): boolean {
    const types = (this.plan?.fileTypes ?? []).map(f => f.toUpperCase());
    return types.length === 0 || this.areAllValuesSelected(types, StepCombinedSelectComponent.SELECTABLE_FILE_TYPES);
  }

  private static readonly SELECTABLE_FILE_TYPES = ['IFC', 'PDF', 'EXCEL', 'WORD', 'POWERPOINT'];

  private areAllValuesSelected(selectedValues: string[], allValues: string[]): boolean {
    return allValues.length > 0 && allValues.every(value => selectedValues.includes(value));
  }

  /** Max. erlaubte Dateien aus dem Plan (>0), sonst undefined = keine Begrenzung. */
  get maxFilesLimit(): number | undefined {
    const max = this.plan?.maxFiles;
    return typeof max === 'number' && max > 0 ? max : undefined;
  }

  /** Dateiauswahl-Modus, um bei Angabe direkt mit der Höchstgrenze zusammengefasst (z.B. "Dateiauswahl manuell - max. 10 Dateien"). */
  get fileModeSummary(): string {
    const mode = this.isFileManual ? 'Dateiauswahl manuell' : 'Vordefinierte Dateiauswahl';
    if (this.maxFilesLimit === undefined) return mode;
    return `${mode} - max. ${this.maxFilesLimit} Datei${this.maxFilesLimit === 1 ? '' : 'en'}`;
  }

  /** True, wenn im manuellen Dateimodus mehr Dateien gewählt sind als erlaubt. */
  get exceedsMaxFiles(): boolean {
    return this.isFileManual
      && this.maxFilesLimit !== undefined
      && this.selectedFiles.length > this.maxFilesLimit;
  }

  // ── Regeln: Anzeige & Auswahl ─────────────────────────────────────────────

  get filteredRuleGroups(): RuleGroup[] {
    const q = this.ruleSearch.trim().toLowerCase();
    const filtered = q
      ? this.ruleGroups.filter(g =>
        g.ruleset.name.toLowerCase().includes(q) ||
        g.ruleset.description?.toLowerCase().includes(q) ||
        g.rules.some(r => r.name.toLowerCase().includes(q)),
      )
      : this.ruleGroups;
    return [...filtered].sort((a, b) => a.ruleset.name.localeCompare(b.ruleset.name, 'de', {numeric: true}));
  }

  /** Tatsächlich ausgewählte Dateien (Ordner bereits zu ihren enthaltenen Dateien aufgelöst, s. {@link onBicSelectionChange}). */
  get selectedFileCount(): number {
    return this.selectedFiles.length;
  }

  private loadFiles(): void {
    // fileTypes aus dem Plan: leer oder nicht gesetzt → alle Dateien anzeigen.
    // Vergleich case-insensitiv, da Manifest-Werte (z.B. "EXCEL") und API-Werte übereinstimmen.
    const fileTypes = this.plan?.fileTypes?.length
      ? this.plan.fileTypes.map(t => t.toUpperCase())
      : null;
    // plan.documentClasses enthält Dokumentenklassen-IDs (siehe my-checks-builder), file.documentClassTitles
    // dagegen nur Titel – daher IDs hier über die geladenen Klassen in Titel auflösen, bevor verglichen wird.
    const allowedDocumentClasses = this.plan?.documentClasses?.length
      ? new Set(this.plan.documentClasses
          .map(id => this.normalizeClassToken(this.resolveDocumentClassLabel(id)))
          .filter(Boolean))
      : null;
    this.filesLoading = true;
    this.fileService.getFiles(this.projectId).subscribe({
      next: (files) => {
        this.files = files.filter(f =>
          (fileTypes === null || fileTypes.includes((f.fileType ?? '').toUpperCase()))
          && this.fileMatchesDocumentClasses(f, allowedDocumentClasses)
          // Nur fertig klassifizierte Dateien anzeigen: der Status wird in dieser Ansicht bewusst
          // nicht als Spalte/Filter angezeigt, weil er hier immer READY sein muss.
          && f.fileStatus === ProjectFile.FileStatusEnum.READY
        );
        // Übergeordnete Ordner ergänzen (die GESAMTE Vorfahrenkette, nicht nur den direkten Elternordner),
        // damit der Datei-Baum dargestellt werden kann.
        this.files.forEach(file => addMissingAncestorFolders(this.files, files, file));
        this.bicFiles = this.mapToBicFiles(this.files);
        this.updateDisplayedBicFiles();
        // Snapshot speichern, damit start() die Dateien auch dann hat, wenn Angular-Timing-Issues auftreten.
        this.predefinedFileSnapshot = this.predefinedFiles;
        // Im vordefinierten Modus gibt es keine interaktive Auswahl. Damit ALLE nachgelagerten
        // Pfade (Zusammenfassungen, Persistenz, Fallbacks) konsistent die Dateien kennen – und im
        // Modus "Alle Dateien prüfen" (Scope 'all') nicht versehentlich eine leere Auswahl entsteht –
        // werden die vordefinierten Dateien direkt als ausgewählt übernommen.
        if (!this.isFileManual) {
          this.selectedFiles = [...this.predefinedFiles];
          this.bicSelectedFiles = this.bicFiles.filter(b => !b.isFolder);
        }
        this.filesLoading = false;
      },
      error: () => {
        this.files = [];
        this.bicFiles = [];
        this.filesLoading = false;
      },
    });
  }

  get totalAvailableRules(): number {
    return this.ruleGroups.reduce((sum, g) => sum + this.effectiveRuleCount(g), 0);
  }

  get totalSelectedRules(): number {
    let count = 0;
    for (const group of this.ruleGroups) {
      if (this.selectedSetIds.has(group.ruleset.id)) {
        count += this.effectiveRuleCount(group);
      } else {
        count += group.rules.filter(r => this.selectedRuleIds.has(r.id)).length;
      }
    }
    return count;
  }

  isExpanded(group: RuleGroup): boolean {
    return this.expandedRulesetIds.has(group.ruleset.id);
  }

  toggleExpand(group: RuleGroup): void {
    if (this.expandedRulesetIds.has(group.ruleset.id)) {
      this.expandedRulesetIds.delete(group.ruleset.id);
      return;
    }
    this.expandedRulesetIds.add(group.ruleset.id);
    this.loadGroupRules(group);
  }

  isSetSelected(group: RuleGroup): boolean {
    return this.selectedSetIds.has(group.ruleset.id);
  }

  isSetPartiallySelected(group: RuleGroup): boolean {
    if (this.isSetSelected(group)) return false;
    return group.rules.some(r => this.selectedRuleIds.has(r.id));
  }

  toggleSet(group: RuleGroup): void {
    if (!this.isRuleManual) return;
    if (this.selectedSetIds.has(group.ruleset.id)) {
      this.selectedSetIds.delete(group.ruleset.id);
    } else {
      this.selectedSetIds.add(group.ruleset.id);
      // Einzelauswahl dieses Sets aufheben – das ganze Set deckt sie ab.
      group.rules.forEach(r => this.selectedRuleIds.delete(r.id));
      // Regeln nachladen, damit beim Bestätigen die IDs vorliegen.
      this.loadGroupRules(group);
    }
  }

  isRuleSelected(group: RuleGroup, rule: RulesetRule): boolean {
    return this.selectedSetIds.has(group.ruleset.id) || this.selectedRuleIds.has(rule.id);
  }

  toggleRule(group: RuleGroup, rule: RulesetRule): void {
    if (!this.isRuleManual) return;
    if (this.selectedSetIds.has(group.ruleset.id)) {
      // Vollauswahl → Teilauswahl: Set entfernen, alle anderen Regeln übernehmen.
      this.selectedSetIds.delete(group.ruleset.id);
      group.rules.forEach(r => {
        if (r.id !== rule.id) this.selectedRuleIds.add(r.id);
      });
      return;
    }
    if (this.selectedRuleIds.has(rule.id)) {
      this.selectedRuleIds.delete(rule.id);
    } else {
      this.selectedRuleIds.add(rule.id);
      // Alle Regeln ausgewählt → zum Set hochstufen.
      if (group.rules.length > 0 && group.rules.every(r => this.selectedRuleIds.has(r.id))) {
        group.rules.forEach(r => this.selectedRuleIds.delete(r.id));
        this.selectedSetIds.add(group.ruleset.id);
      }
    }
  }

  selectAllRules(): void {
    if (!this.isRuleManual) return;
    const allSelected = this.ruleGroups.every(g => this.selectedSetIds.has(g.ruleset.id));
    if (allSelected) {
      this.selectedSetIds.clear();
      this.selectedRuleIds.clear();
    } else {
      this.ruleGroups.forEach(g => {
        this.selectedSetIds.add(g.ruleset.id);
        this.loadGroupRules(g);
      });
    }
  }

  getTypeBadgeLabel(type: string): string {
    switch (type?.toUpperCase()) {
      case 'HYBRID':
        return 'Hybride Prüfung';
      case 'KI':
        return 'KI Prüfung';
      case 'IDS':
        return 'IDS Prüfung';
      default:
        return type;
    }
  }

  getTypeBadgeClass(type: string): string {
    switch (type?.toUpperCase()) {
      case 'HYBRID':
        return 'bg-blue-500 text-white';
      case 'KI':
        return 'bg-blue-100 text-gray-700';
      default:
        return 'bg-gray-200 text-gray-700';
    }
  }

  // ── Dateien: Zusammenfassung ──────────────────────────────────────────────

  get totalSelectedFiles(): number {
    return this.selectedFiles.length;
  }

  get totalFileSizeLabel(): string {
    const bytes = this.selectedFiles.reduce((sum, f) => sum + (f.size ?? 0), 0);
    return this.formatBytes(bytes);
  }

  formatBytes(bytes?: number): string {
    if (!bytes || bytes === 0) return '–';
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  }

  /** Nur echte Dateien (keine Ordner) für die Vordefiniert-Ansicht. */
  get predefinedFiles(): ProjectFile[] {
    return this.files.filter(f => !f.isFolder && f.fileType !== ProjectFile.FileTypeEnum.FOLDER);
  }

  /** Dateien nach Typ gruppiert, sortiert nach Anzahl absteigend. */
  get groupedPredefinedFiles(): { label: string; fileType: string; files: ProjectFile[]; totalSize: number }[] {
    const map = new Map<string, ProjectFile[]>();
    for (const f of this.predefinedFiles) {
      const key = (f.fileType ?? 'UNKNOWN').toUpperCase();
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(f);
    }
    return Array.from(map.entries())
      .map(([fileType, files]) => ({
        fileType,
        label: this.getFileTypeGroupLabel(fileType),
        files,
        totalSize: files.reduce((sum, f) => sum + (f.size ?? 0), 0),
      }))
      .sort((a, b) => b.files.length - a.files.length);
  }

  get totalPredefinedSize(): number {
    return this.predefinedFiles.reduce((sum, f) => sum + (f.size ?? 0), 0);
  }

  /** Menschenlesbarer Gruppen-Titel je Dateityp (z.B. "IFC-Modelle", "PDF-Dokumente"). */
  getFileTypeGroupLabel(fileType: string): string {
    switch (fileType.toUpperCase()) {
      case 'IFC': return 'IFC-Modelle';
      case 'PDF': return 'PDF-Dokumente';
      case 'EXCEL': return 'Excel-Dateien';
      case 'WORD': return 'Word-Dokumente';
      case 'POWERPOINT': return 'PowerPoint-Dateien';
      case 'COORDINATION': return 'Koordinationsdateien';
      default: return 'Weitere Dateien';
    }
  }

  /** Icon je Dateityp. */
  getFileIcon(fileType?: string) {
    switch ((fileType ?? '').toUpperCase()) {
      case 'PDF': return this.faFilePdf;
      case 'EXCEL': return this.faFileExcel;
      case 'WORD': return this.faFileWord;
      case 'POWERPOINT': return this.faFileSlides;
      case 'IFC': return this.faIfc;
      default: return this.faFile;
    }
  }

  /** Mapping API-Enum-Wert → Anzeigelabel */
  private readonly FILE_TYPE_LABELS: Record<string, string> = {
    IFC: 'IFC', PDF: 'PDF', EXCEL: 'Excel', WORD: 'Word',
    POWERPOINT: 'PowerPoint', COORDINATION: 'Coordination', UNKNOWN: 'Unbekannt',
  };

  get canStart(): boolean {
    const rulesOk = !this.isRuleManual || this.totalSelectedRules > 0;
    const filesOk = !this.isFileManual || this.selectedFiles.length > 0;
    // Im vordefinierten Dateimodus muss das Laden abgeschlossen sein, bevor gestartet werden kann.
    const filesReady = this.isFileManual || (!this.filesLoading && this.predefinedFileSnapshot.length > 0);
    return rulesOk && filesOk && filesReady && !this.exceedsMaxFiles && this.hasSufficientCredits
      && this.rulesetsWithoutMatchingFile.length === 0;
  }

  /**
   * Die effektiv zu prüfenden Dateien – bei manueller Auswahl die markierten, sonst die
   * vordefinierte Menge.
   */
  private get effectiveFiles(): ProjectFile[] {
    if (this.isFileManual) return this.selectedFiles;
    return this.predefinedFiles.length > 0 ? this.predefinedFiles : this.predefinedFileSnapshot;
  }

  /**
   * Namen der ausgewählten Regelsets, für die **keine** der zu prüfenden Dateien passt.
   *
   * Produktentscheidung: Ein Regelset ohne passende Datei blockiert den Start – statt es still mit
   * einem leeren Teilbericht mitlaufen zu lassen. Sonst wirkt der Bericht vollständig, obwohl ein
   * Regelset faktisch nichts geprüft hat.
   */
  get rulesetsWithoutMatchingFile(): string[] {
    const files = this.effectiveFiles;
    // Solange nichts ausgewählt/geladen ist, greifen die bestehenden Meldungen – nicht doppelt melden.
    if (files.length === 0) return [];
    return this.selectedRuleGroups
      .filter(group => filesForRuleset(group.ruleset, files, this.documentClassTitleById).length === 0)
      .map(group => group.ruleset.name);
  }

  /** Die Regelset-Gruppen, die tatsächlich Teil der Auswahl sind (ganz oder teilweise). */
  private get selectedRuleGroups(): RuleGroup[] {
    return this.ruleGroups.filter(group =>
      this.selectedSetIds.has(group.ruleset.id)
      || group.rules.some(r => this.selectedRuleIds.has(r.id)));
  }

  /** Erlaubte Dateiformate eines Regelsets für die Blocker-Meldung. */
  allowedFileTypesLabel(ruleset: Ruleset): string {
    const types = rulesetAllowedFileTypes(ruleset);
    return types.length > 0 ? types.join(', ') : 'alle Formate';
  }

  get fileTypeSummary(): string {
    if (this.isAllFileTypes) return 'Alle erlaubt';
    const types = this.plan?.fileTypes?.length ? this.plan.fileTypes : [];
    return types.map(t => this.FILE_TYPE_LABELS[t.toUpperCase()] ?? t).join(', ') || 'Alle erlaubt';
  }

  getRulesetFileFormatsLabel(ruleset: Ruleset): string {
    const values = (ruleset.allowedFileTypes ?? [])
      .map(v => this.normalizeRulesetFileTypeToken(v))
      .filter(Boolean);
    const unique = Array.from(new Set(values));
    return unique.length > 0 ? unique.join(', ') : 'Alle Dateiformate';
  }

  getRulesetDocumentClassesLabel(ruleset: Ruleset): string {
    const values = (ruleset.allowedDocumentClasses ?? [])
      .map(v => this.resolveDocumentClassLabel(String(v ?? '').trim()))
      .filter(Boolean);
    const unique = Array.from(new Set(values));
    return unique.length > 0 ? unique.join(', ') : 'Alle Dokumentenklassen';
  }

  /**
   * Delegiert an die gemeinsame Normalisierung. Anders als dort wird ein unbekannter Wert hier
   * unverändert (groß) zurückgegeben – die Anzeige soll auch exotische Angaben aus einem Regelset
   * nennen, statt sie zu verschlucken.
   */
  private normalizeRulesetFileTypeToken(value: string): string {
    return normalizeFileTypeToken(value) || String(value ?? '').trim().toUpperCase();
  }

  private loadDocumentClasses(onComplete?: () => void): void {
    if (!this.projectId) {
      this.documentClassTitleById.clear();
      this.documentClassesLoading = false;
      onComplete?.();
      return;
    }

    this.documentClassService.list(this.projectId).subscribe({
      next: (response) => {
        const titleMap = new Map<string, string>();
        for (const documentClass of (response.classes ?? [])) {
          const id = this.normalizeClassToken(documentClass.id);
          const title = String(documentClass.title ?? '').trim();
          if (!id || !title) continue;
          titleMap.set(id, title);
          // Global geteilte Checks referenzieren Standardklassen als stabiles seed:<KEY>-Token
          // statt über die projektgebundene UUID — beide Schreibweisen auf denselben Titel mappen.
          if (documentClass.seedKey) {
            titleMap.set(this.normalizeClassToken('seed:' + documentClass.seedKey), title);
          }
        }
        this.documentClassTitleById = titleMap;
        this.documentClassesLoading = false;
        onComplete?.();
      },
      error: () => {
        this.documentClassTitleById.clear();
        this.documentClassesLoading = false;
        onComplete?.();
      },
    });
  }

  private resolveDocumentClassLabel(value: string): string {
    const key = this.normalizeClassToken(value);
    return this.documentClassTitleById.get(key) ?? value;
  }

  private fileMatchesDocumentClasses(file: ProjectFile, allowedDocumentClasses: Set<string> | null): boolean {
    if (allowedDocumentClasses === null) return true;

    const fileClasses = new Set<string>();
    for (const title of file.documentClassTitles ?? []) {
      const normalized = this.normalizeClassToken(title);
      if (normalized) fileClasses.add(normalized);
    }

    if (fileClasses.size === 0) return false;
    for (const value of fileClasses) {
      if (allowedDocumentClasses.has(value)) return true;
    }
    return false;
  }

  /** Zeile 2 der Prüfregeln-Karte: "X Prüfregeln aus Y myChecks" – immer der Plan-Rahmen, unabhängig von der aktuellen Selektion. */
  get ruleSummaryLine(): string {
    const sets = this.ruleGroups.length;
    const rules = this.ruleGroups.reduce((sum, g) => sum + Math.max(g.ruleset.ruleCount, g.rules.length), 0);
    return `${rules} Prüfregel${rules !== 1 ? 'n' : ''} aus ${sets} myCheck${sets !== 1 ? 's' : ''}`;
  }

  /** Menschenlesbares Label für die Berichtsperspektive. */
  get reportPerspectiveLabel(): string {
    switch (this.plan?.reportPerspective) {
      case 'files_first':
        return 'Zuerst Dateien, dann Prüfregeln';
      case 'structure_first':
        return 'Zuerst Dateistrukturen, dann Prüfregeln';
      default:
        return 'Zuerst Prüfregeln, dann Dateien';
    }
  }

  /** Kommaseparierte Liste der aktiven Berichtsformate (z. B. "EXCEL + PDF"). */
  get reportFormatsLabel(): string {
    const formats = this.plan?.reportFormats ?? [];
    if (formats.length === 0) return 'In-App';
    return formats.join(' + ');
  }

  // ── Bestätigung ────────────────────────────────────────────────────────────

  /** Tooltip-Text für den deaktivierten "Prüfung starten"-Button; nennt den fehlenden Grund. */
  get startDisabledReason(): string | undefined {
    if (this.canStart) return undefined;
    const rulesOk = !this.isRuleManual || this.totalSelectedRules > 0;
    const filesOk = !this.isFileManual || this.selectedFiles.length > 0;
    if (!rulesOk && !filesOk) return 'Mindestens 1 myCheck und eine Datei auswählen';
    if (!rulesOk) return 'Mindestens 1 myCheck auswählen';
    if (!filesOk) return 'Mindestens eine Datei auswählen';
    if (this.exceedsMaxFiles) return `Maximal ${this.maxFilesLimit} Datei${this.maxFilesLimit === 1 ? '' : 'en'} erlaubt`;
    if (!this.hasSufficientCredits) return 'Nicht genügend Prüfungen verfügbar';
    const blocked = this.rulesetsWithoutMatchingFile;
    if (blocked.length > 0) {
      return blocked.length === 1
        ? `Für „${blocked[0]}" ist keine passende Datei ausgewählt`
        : `Für ${blocked.length} myChecks ist keine passende Datei ausgewählt`;
    }
    return 'Bitte warten, bis die Dateien geladen sind';
  }

  /** Liest den Wert aus dem nativen 'input'-Event des bic-file-manager-eigenen Suchfelds (bubbelt
   *  bis zum Tag hoch) – so bleibt dessen Original-Layout unverändert, statt ein eigenes Suchfeld
   *  danebenzusetzen. */
  onFileManagerSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    if (!target || target.tagName !== 'INPUT') return;
    this.fileSearchTerm = target.value ?? '';
    this.updateDisplayedBicFiles();
  }

  /** Liest {@link fileColumnFilters} neu aus dem bic-file-manager aus (s. {@link onFileManagerCaptureClick}). */
  private syncColumnFiltersFromFileManager(): void {
    const current = this.bicFileManagerRef?.columnFilters ?? {};
    const json = JSON.stringify(current);
    if (json === this.lastColumnFiltersJson) return;
    this.lastColumnFiltersJson = json;
    this.fileColumnFilters = current;
    this.updateDisplayedBicFiles();
  }

  /** Ohne Suchbegriff/Filter die volle Baumstruktur, sonst eine flache, projektweite Trefferliste
   *  über alle Ordnerebenen (statt nur den aktuell geöffneten Ordner, s. bic-file-manager-Einschränkung). */
  private updateDisplayedBicFiles(): void {
    const term = this.fileSearchTerm.trim().toLowerCase();
    const hasFilters = Object.values(this.fileColumnFilters).some(v => v && v.length > 0);
    if (!term && !hasFilters) {
      this.displayedBicFiles = this.bicFiles;
      return;
    }
    this.displayedBicFiles = this.mapToBicFiles(
      this.files.filter(f =>
        !this.isFolderFile(f)
        && (!term || (f.name ?? '').toLowerCase().includes(term))
        && this.fileMatchesColumnFilters(f)),
    ).map(f => ({...f, parentId: undefined}));
    // Ohne Reset bliebe bic-file-manager im zuletzt geöffneten Ordner und die (jetzt elternlose) Trefferliste wäre unsichtbar.
    this.bicFileManagerRef?.navigateToFolder({file: undefined, depth: 0});
  }

  /** Prüft eine Datei gegen {@link fileColumnFilters} – spiegelt die Matching-Logik von
   *  bic-file-manager's `applyColumnFiltersToData` (nicht über dessen Komponentengrenze aufrufbar). */
  private fileMatchesColumnFilters(file: ProjectFile): boolean {
    return Object.entries(this.fileColumnFilters).every(([key, values]) => {
      if (!values || values.length === 0) return true;
      if (key === 'lastModified') {
        return !!file.lastModified && values.some(range => this.isDateInRange(file.lastModified, range));
      }
      const raw = (file as any)[key];
      if (Array.isArray(raw)) return values.some(v => raw.includes(v));
      return values.includes(raw);
    });
  }

  /** Muss mit bic-file-manager's internen Datumsbereichs-Definitionen übereinstimmen. */
  private isDateInRange(value: unknown, range: string): boolean {
    const date = new Date(value as any);
    if (isNaN(date.getTime())) return false;
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    switch (range) {
      case 'Heute':
        return d.getTime() === today.getTime();
      case 'Letzte 7 Tage': {
        const ago = new Date(today);
        ago.setDate(today.getDate() - 7);
        return d >= ago;
      }
      case 'Letzte 30 Tage': {
        const ago = new Date(today);
        ago.setDate(today.getDate() - 30);
        return d >= ago;
      }
      case 'Dieses Jahr':
        return d.getFullYear() === today.getFullYear();
      case 'Vor diesem Jahr':
        return d.getFullYear() < today.getFullYear();
      default:
        return false;
    }
  }

  /** Uebernimmt die Auswahl aus dem bic-file-manager und spiegelt sie auf ProjectFile[]. */
  onBicSelectionChange(selected: BicFile[]): void {
    // Die Auswahl des bic-file-manager 1:1 spiegeln. WICHTIG: hier NICHT mit einer neu berechneten
    // Liste überschreiben – der Manager verwaltet die Mehrfachauswahl intern über genau dieses Array;
    // ein Ersetzen würde die Selektion bei jedem Klick auf eine einzelne Datei zurücksetzen.
    this.bicSelectedFiles = selected;
    const selectedIds = new Set(selected.map(s => s.id));

    // Für die nachgelagerte Verwendung (Zähler, Prüfzusammenfassung, Payload, maxFiles-Prüfung) nur
    // die echten Dateien übernehmen; der Ordner selbst fließt nie als FOLDER-Datei ein.
    //
    // Ordner werden hier bewusst NICHT zu ihrem Unterbaum aufgelöst: der bic-file-manager läuft mit
    // selectChildrenOnFolderSelect und legt beim Anhaken eines Ordners dessen Dateien einzeln in die
    // Auswahl. Ein Auflösen wäre also nicht nur redundant, es hätte auch jede abgewählte Datei
    // wieder hereingeholt, solange der Ordner noch ausgewählt ist – das Abwählen einzelner Dateien
    // oder Unterordner blieb dadurch ohne Wirkung auf Zähler und maxFiles-Grenze.
    this.selectedFiles = this.files.filter(
      f => !!f.id && selectedIds.has(f.id) && !this.isFolderFile(f));
  }

  /** True, wenn das ProjectFile ein Ordner ist. */
  private isFolderFile(file: ProjectFile): boolean {
    return !!file.isFolder || file.fileType === ProjectFile.FileTypeEnum.FOLDER;
  }

  /**
   * Anzahl der Regelsets, die dieser Lauf prüft – und damit die Anzahl der abgerechneten Einheiten.
   *
   * Ein Lauf über mehrere Regelsets führt pro Regelset einen eigenen Sub-Run aus und wird auch
   * pro Regelset abgebucht (Produktentscheidung; das Backend reserviert entsprechend viele
   * Einheiten). Bei manueller Auswahl zählt die tatsächliche Auswahl, sonst die im myCheck
   * vordefinierte.
   */
  get billedRulesetCount(): number {
    // Ohne Dispatcher im Manifest führt der Lauf nur eine Kette aus und das Backend bucht genau
    // eine Einheit ab – dann wäre eine Anzeige „N × Kosten" falsch.
    if (!this.plan?.usesMultiCheckDispatcher) return 1;
    if (this.isRuleManual) {
      let count = 0;
      for (const group of this.ruleGroups) {
        const fullySelected = this.selectedSetIds.has(group.ruleset.id);
        const partiallySelected = group.rules.some(r => this.selectedRuleIds.has(r.id));
        if (fullySelected || partiallySelected) count++;
      }
      return Math.max(count, 1);
    }
    const predefined = new Set([
      ...(this.plan?.selectedRuleSetIds ?? []),
      ...(this.plan?.partiallySelectedRuleSetIds ?? []),
    ]);
    return Math.max(predefined.size, 1);
  }

  /** Credit-Kosten je Regelset (Pauschalpreis, unabhängig vom Regeltyp). */
  get creditCostPerRuleset(): number {
    return this.stateConfigService.getProjectContingent(this.projectId).myCheckCosts ?? 0;
  }

  /** Gesamtkosten dieses Laufs: Pauschalpreis × Anzahl der geprüften Regelsets. */
  get creditCost(): number {
    return this.creditCostPerRuleset * this.billedRulesetCount;
  }

  /**
   * Kosten dieses Laufs in Prüfungen.
   *
   * <p>Der Umrechnungsfaktor ist der myCheck-Pauschalpreis aus dem Kontingent, nichts
   * Hartcodiertes; er ist zugleich die abgerechnete Einheit, also entspricht das der Anzahl
   * abgerechneter Regelsets. Der Fallback greift, wenn das Modul kostenlos gestellt ist.
   */
  get checkCost(): number {
    const perRuleset = this.creditCostPerRuleset;
    return perRuleset > 0 ? this.creditCost / perRuleset : this.billedRulesetCount;
  }

  /**
   * Verbleibende Prüfungen nach diesem Lauf.
   *
   * <p>`myChecks` ist die einheitliche Restanzeige: während einer Testphase der Trial-Zähler,
   * danach `(credits + addOnCredits) / myCheckCosts` (siehe `Contingent.getMyChecks()`). Damit
   * entfällt die frühere Unterscheidung zwischen Testprüfungen und Credits – gezählt wird in beiden
   * Fällen dasselbe.
   */
  get checksAfterRun(): number {
    const affordable = this.stateConfigService.getProjectContingent(this.projectId).myChecks ?? 0;
    return Math.max(affordable - this.billedRulesetCount, 0);
  }

  get hasSufficientCredits(): boolean {
    // canCheckMyCheck() prüft nur, ob EIN Lauf gedeckt ist. Ein gemischter Lauf kostet pro
    // Regelset – die Reservierung im Backend ist alles-oder-nichts, also muss die Deckung hier
    // für alle Einheiten geprüft werden, sonst wird der Start mit 402 abgelehnt.
    const affordableRuns = this.stateConfigService.getProjectContingent(this.projectId).myChecks ?? 0;
    return affordableRuns >= this.billedRulesetCount;
  }


  /**
   * Name, den der Bericht ohne eigene Eingabe erhält: der myCheck-Name. Bis 10/2026 hing hier das
   * Startdatum an; die Berichtsübersicht zeigt das Datum inzwischen in einer eigenen Spalte (#2541).
   */
  get defaultReportName(): string {
    return this.plan?.checkName ?? 'myCheck';
  }

  /** Der Name, unter dem der Bericht tatsächlich erscheinen wird - für die Vorschau im Popup. */
  get effectiveReportName(): string {
    return this.reportName.trim() || this.defaultReportName;
  }

  showConfirmation(): void {
    if (!this.canStart || this.confirming) return;
    // Nur zugeklappt starten, wenn noch kein eigener Name vergeben wurde - sonst würde ein
    // erneutes Öffnen des Popups den bereits eingetippten Namen hinter dem Trigger verstecken.
    this.showReportNameInput = !!this.reportName.trim();
    this.showConfirmPopup = true;
  }

  start(): void {
    if (!this.canStart || this.confirming) return;
    this.showConfirmPopup = false;
    this.confirming = true;

    // IDS-Sonderfall: Wenn innerhalb eines IDS-Sets nur einzelne Spezifikationen (nicht das ganze
    // Set) ausgewählt sind, existiert dafür keine eigenständige Backend-Regel-ID (eine Spezifikation
    // ist nur als <specification> in der einen XML des Regelsets eingebettet). In dem Fall muss vorab
    // eine auf die Auswahl reduzierte IDS-XML geladen/gebaut werden, bevor der Rest synchron weiterläuft.
    //
    // Bewusst über ALLE teilselektierten IDS-Sets: früher wurde nur das erste behandelt, womit die
    // Teilauswahl aller weiteren IDS-Sets spurlos verloren ging.
    const partialIdsGroups = this.findPartialIdsSelectionGroups();
    if (partialIdsGroups.length === 0) {
      this.finishStart();
      return;
    }

    const xmlRequests = partialIdsGroups.map(group =>
      this.rulesetService.getIdsRulesetXml(group.ruleset.id, this.projectId));
    forkJoin(xmlRequests).subscribe({
      next: (rawXmls) => {
        const xmlByRulesetId: Record<string, string> = {};
        partialIdsGroups.forEach((group, index) => {
          const rawXml = rawXmls[index];
          if (!rawXml) return;
          const specNames = this.selectedSpecNames(group);
          xmlByRulesetId[group.ruleset.id] = this.idsAdapter.filterSpecificationsByName(rawXml, specNames);
        });
        this.finishStart(xmlByRulesetId);
      },
      // Ohne XML kann die Teilauswahl nicht geprüft werden – der Lauf startet trotzdem, das Backend
      // meldet dann die fehlende Regel. Verhalten wie bisher.
      error: () => this.finishStart({}),
    });
  }

  /** Alle IDS-Regelsets, bei denen nur einzelne Spezifikationen (nicht das ganze Set) gewählt sind. */
  private findPartialIdsSelectionGroups(): RuleGroup[] {
    return this.ruleGroups.filter(group =>
      group.ruleset.type === 'IDS'
      && !this.selectedSetIds.has(group.ruleset.id)
      && group.rules.some(r => this.selectedRuleIds.has(r.id)));
  }

  /** Namen der innerhalb einer Gruppe einzeln ausgewählten Regeln/Spezifikationen. */
  private selectedSpecNames(group: RuleGroup): string[] {
    return group.rules.filter(r => this.selectedRuleIds.has(r.id)).map(r => r.name);
  }

  /**
   * Baut das Resume-/Trigger-Payload und emittiert das Ergebnis.
   *
   * @param idsXmlByRulesetId Bei IDS-Teilauswahlen: je Regelset die clientseitig auf die gewählten
   *   Spezifikationen reduzierte XML (siehe {@link findPartialIdsSelectionGroups} / {@link start}).
   */
  private finishStart(idsXmlByRulesetId: Record<string, string> = {}): void {
    const result: CombinedSelectionResult = {
      ruleSummaries: [],
      fileSummaries: [],
    };

    // ── Regel-Resume-Payload (nur bei manueller Regelauswahl) ──
    if (this.isRuleManual) {
      const groups = this.collectRuleGroups(idsXmlByRulesetId);
      const ruleData: Record<string, unknown> = {};

      if (groups.length > 0) {
        // Gruppierte Auswahl: ein Eintrag je Regelset. Der Dispatcher (multi_check_executor)
        // startet daraus je Gruppe einen Sub-Run mit der typrichtigen Kette.
        ruleData['rule_groups'] = groups;

        // Zusätzlich die flachen, eintypigen Felder aus der ERSTEN Gruppe: die bestehenden
        // typspezifischen Ketten (Ein-Regelset-Checks, Alt-Manifeste) lesen weiterhin diese.
        const first = groups[0];
        ruleData['rule_type'] = first.ruleset_type;
        ruleData['ruleset_type'] = first.ruleset_type;
        ruleData['rule_name'] = first.rule_names[0];
        if (first.rule_names.length > 1) ruleData['rule_names'] = first.rule_names;

        if (first.ids_xml) {
          // Die synthetischen Spezifikations-IDs (rulesetId::specName) lassen sich im Backend nicht
          // auflösen – rule_id/rule_ids/ruleset_id weglassen, damit dort kein (fehlschlagender oder
          // die gefilterte XML überschreibender) Auflösungsversuch passiert. Stattdessen reist die
          // bereits gefilterte XML als Regelinhalt mit.
          ruleData['ids_xml'] = first.ids_xml;
          ruleData['rule_name'] = first.rule_names.join(', ');
          ruleData['rule_names'] = first.rule_names;
        } else {
          ruleData['rule_id'] = first.rule_ids[0];
          ruleData['ruleset_id'] = first.ruleset_id;
          if (first.rule_ids.length > 1) ruleData['rule_ids'] = first.rule_ids;
        }
      }

      result.ruleData = ruleData;
      result.ruleSummaries = this.buildGroupedRuleSummaries(true);
    } else {
      result.ruleSummaries = this.buildGroupedRuleSummaries(false);
    }

    // ── Datei-Resume-Payload (nur bei manueller Dateiauswahl) ──
    if (this.isFileManual) {
      const projectFiles = this.selectedFiles.map(f => ({
        id: f.id,
        name: f.name,
        link: f.link,
        fileType: f.fileType,
        size: f.size,
      }));
      result.fileData = {project_files: projectFiles};
      result.fileSummaries = this.selectedFiles.map(f => ({
        name: f.name ?? '',
        fileType: f.fileType ?? 'IFC',
        size: f.size,
      }));
    } else {
      // Vordefinierter Dateimodus: Dateien als Trigger-Daten mitsenden, damit das Backend sie
      // beim internen file_selection-Step aus trigger_0.outputs.project_files lesen kann.
      // Wichtig: Bevorzugt die aktuell geladene Liste (this.predefinedFiles), die identisch zur
      // angezeigten Datei-Übersicht ist. Der Snapshot dient nur als Fallback, falls die Live-Liste
      // (z. B. durch Timing) ausnahmsweise leer ist. Dadurch wird sichergestellt, dass im Modus
      // "Alle Dateien prüfen" (Scope 'all') genau die angezeigten Dateien auch übergeben werden.
      const filesToSend = this.predefinedFiles.length > 0
        ? this.predefinedFiles
        : this.predefinedFileSnapshot;
      const predefinedProjectFiles = filesToSend.map(f => ({
        id: f.id,
        name: f.name,
        link: f.link,
        fileType: f.fileType,
        size: f.size,
      }));
      result.triggerData = {project_files: predefinedProjectFiles};
      result.fileSummaries = filesToSend.map(f => ({
        name: f.name ?? '',
        fileType: f.fileType ?? '',
        size: f.size,
      }));
    }

    // Immer einen konkreten Namen senden statt bei leerer Eingabe nichts zu übergeben: sonst würde
    // das Backend selbst benennen (nur der myCheck-Name, ohne Datum) und der Bericht hieße etwas
    // anderes als die Vorschau im Bestätigungs-Popup versprochen hat.
    result.reportName = this.effectiveReportName;

    this.started.emit(result);
  }

  cancel(): void {
    this.cancelled.emit();
  }

  /**
   * Sammelt die Auswahl **gruppiert pro Regelset** – eine Gruppe je beteiligtem Set.
   *
   * Ersetzt die frühere flache Sammlung, die alle Sets in eine Tüte geworfen hat: dort überlebte
   * nur die erste `ruleset_id` und der Typ des letzten Sets, sodass ID und Typ auf verschiedene
   * Regelsets zeigen konnten und die Sets 2..N nie beim Orchestrator ankamen.
   *
   * @param idsXmlByRulesetId gefilterte IDS-XML je teilselektiertem IDS-Set
   */
  private collectRuleGroups(idsXmlByRulesetId: Record<string, string> = {}): RuleGroupPayload[] {
    const groups: RuleGroupPayload[] = [];

    for (const group of this.ruleGroups) {
      const ruleIds: string[] = [];
      const ruleNames: string[] = [];
      const setSelected = this.selectedSetIds.has(group.ruleset.id);

      if (setSelected) {
        if (group.ruleset.type === 'IDS') {
          // IDS-Spezifikationen haben keine eigenständige, auflösbare Regel-ID (siehe
          // RulesetRule.id = "<rulesetId>::<specName>" in ruleset.service.ts). Bei vollständiger
          // Set-Auswahl deshalb immer die echte Regelset-ID verwenden - unabhängig davon, ob die
          // Spezifikationen zur Anzeige bereits geladen wurden (z.B. weil das Set aufgeklappt war).
          // Die Namen dagegen sollen, wenn verfügbar, die echten Spezifikationsnamen sein (nicht den
          // Regelset-Namen) - sonst zeigt der Ladebildschirm nach einem frischen Reload (der die
          // Auswahl aus rule_names der Instanz rekonstruiert) den Regelset-Namen doppelt an.
          ruleIds.push(group.ruleset.id);
          if (group.rules.length > 0) {
            ruleNames.push(...group.rules.map(r => r.name));
          } else {
            ruleNames.push(group.ruleset.name);
          }
        } else if (group.rules.length > 0) {
          group.rules.forEach(r => {
            ruleIds.push(r.id);
            ruleNames.push(r.name);
          });
        } else {
          // Regeln (noch) nicht geladen → Set-ID als Regel verwenden (Hybrid).
          ruleIds.push(group.ruleset.id);
          ruleNames.push(group.ruleset.name);
        }
      } else {
        const selectedInGroup = group.rules.filter(r => this.selectedRuleIds.has(r.id));
        if (selectedInGroup.length === 0) continue;
        selectedInGroup.forEach(r => {
          ruleIds.push(r.id);
          ruleNames.push(r.name);
        });
      }

      const payload: RuleGroupPayload = {
        ruleset_id: group.ruleset.id,
        ruleset_name: group.ruleset.name,
        ruleset_type: group.ruleset.type,
        rule_ids: ruleIds,
        rule_names: ruleNames,
        // Datei-Partitionierung: jedes Regelset bekommt nur die Dateien, die es prüfen kann
        // (IDS → IFC, KI meist PDF/Office). Ohne das würde jeder Sub-Run alle Dateien erhalten
        // und an den unpassenden scheitern bzw. sie sinnlos mitverarbeiten.
        project_files: filesForRuleset(group.ruleset, this.effectiveFiles, this.documentClassTitleById)
          .map(f => ({id: f.id, name: f.name, link: f.link, fileType: f.fileType, size: f.size})),
      };
      const idsXml = idsXmlByRulesetId[group.ruleset.id];
      if (idsXml) payload.ids_xml = idsXml;
      groups.push(payload);
    }

    return groups;
  }

  /**
   * Baut die Zusammenfassung der Regeln gruppiert pro Regelset auf, sodass der
   * Ladebildschirm jedes Set mit den darunter eingerueckten Einzelregeln anzeigen kann.
   * @param manualOnly true = nur die tatsaechlich ausgewaehlten Regeln/Sets; false = alle (vordefiniert).
   */
  private buildGroupedRuleSummaries(manualOnly: boolean):
    { name: string; type: string; ruleNames?: string[]; rulesetId?: string }[] {
    const summaries: { name: string; type: string; ruleNames?: string[]; rulesetId?: string }[] = [];
    for (const group of this.ruleGroups) {
      let ruleNames: string[];
      if (manualOnly) {
        const setSelected = this.selectedSetIds.has(group.ruleset.id);
        if (setSelected) {
          ruleNames = group.rules.map(r => r.name);
        } else {
          ruleNames = group.rules.filter(r => this.selectedRuleIds.has(r.id)).map(r => r.name);
          // Set weder komplett noch teilweise gewaehlt -> ueberspringen.
          if (ruleNames.length === 0) continue;
        }
      } else {
        ruleNames = group.rules.map(r => r.name);
      }
      summaries.push({
        name: group.ruleset.name,
        type: group.ruleset.type,
        ruleNames: ruleNames.length > 0 ? ruleNames : undefined,
        // Schlüssel für die Zuordnung von Sub-Run-Fortschritt (WorkflowEvent.subRun.rulesetId)
        // zur jeweiligen Regelset-Gruppe im Fortschritts-/Log-Bildschirm.
        rulesetId: group.ruleset.id,
      });
    }
    return summaries;
  }
}
